import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ChevronDown, ArrowLeft } from 'lucide-react';
import ApiService from '../../components/api_services/apiService.jsx';
import { fetchFirmaByNip } from '../../components/api_services/gusService.js';
import { showAlert } from '../../components/components/global_alert/alertBus';
import { UI_PLAN_TO_API, UI_PLAN_DURATION_DAYS, STRIPE_PUBLISHABLE_KEY, buildPromotionFormDataFromProfile, EMPTY_PROMOTION_FORM_DATA, formatPromotionDate, isScheduledPromotionStatus, getPromotionStartDateBounds, validatePromotionStartDate, getPromotionTruncationNotice, getApiErrorMessage, toPromotionStartDateIso, isDateWithinPromotionStartBounds } from './promoUtils.js';
import PromotionStartDatePicker from './PromotionStartDatePicker.jsx';
import './paymentTest2.css';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const plans = [
    { id: 'Basic', name: 'Basic', price: 0 },
    { id: 'Standard', name: 'Standard', price: 9 },
    { id: 'Visibility+', name: 'Visibility+', price: 19 },
    { id: 'Premium', name: 'Premium', price: 29 },
];

function PromotionOrderForm({ initialPlan = 'Basic', initialEventId = null, initialInvoiceData = null, onBack, onSuccess }) {
    const stripe = useStripe();
    const elements = useElements();
    const userEmail = useSelector((state) => state.tokens.userEmail);
    const userPhoneNumber = useSelector((state) => state.tokens.userPhoneNumber);
    const [selectedPlan, setSelectedPlan] = useState(initialPlan);
    const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState(false);
    const [events, setEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [formData, setFormData] = useState(EMPTY_PROMOTION_FORM_DATA);
    const [startDate, setStartDate] = useState('');

    const selectedEvent = events.find((event) => String(event.id) === String(selectedEventId));
    const { min: minStartDate, max: maxStartDate } = getPromotionStartDateBounds(selectedEvent);
    const planDurationDays = UI_PLAN_DURATION_DAYS[selectedPlan];
    const truncationNotice = getPromotionTruncationNotice(startDate, planDurationDays, selectedEvent?.endDate);

    const currentPlanData = plans.find((p) => p.id === selectedPlan) || plans[0];
    const total = currentPlanData.price;
    const vatRate = 0.23;
    const basePrice = total / (1 + vatRate);
    const vat = basePrice * vatRate;

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const response = await ApiService.getPromotableEvents();
                const list = response.data || [];
                setEvents(list);
                if (initialEventId) {
                    setSelectedEventId(String(initialEventId));
                } else if (list.length > 0) {
                    setSelectedEventId(String(list[0].id));
                }
            } catch (error) {
                showAlert('Nie udało się pobrać listy wydarzeń.');
            }
        };
        loadEvents();
    }, [initialEventId]);

    useEffect(() => {
        const loadProfile = async () => {
            setIsLoadingProfile(true);
            try {
                const response = await ApiService.getOrganiserProfile();
                const profile = Array.isArray(response) ? response[0] : response;
                setFormData(
                    buildPromotionFormDataFromProfile(
                        profile,
                        { userEmail, userPhoneNumber },
                        initialInvoiceData
                    )
                );
            } catch (error) {
                console.error('Błąd pobierania profilu organizatora:', error);
                if (initialInvoiceData) {
                    setFormData(
                        buildPromotionFormDataFromProfile(
                            null,
                            { userEmail, userPhoneNumber },
                            initialInvoiceData
                        )
                    );
                }
            } finally {
                setIsLoadingProfile(false);
            }
        };

        loadProfile();
    }, [userEmail, userPhoneNumber, initialInvoiceData]);

    useEffect(() => {
        if (!startDate) {
            return;
        }
        const { min, max } = getPromotionStartDateBounds(selectedEvent);
        if (!isDateWithinPromotionStartBounds(startDate, min, max)) {
            setStartDate('');
        }
    }, [selectedEventId, selectedEvent]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleNipBlur = async () => {
        if (!formData.nip || formData.nip.length < 10) return;
        try {
            const firma = await fetchFirmaByNip(formData.nip);
            if (firma) {
                setFormData((prev) => ({
                    ...prev,
                    companyName: firma.nazwa || prev.companyName,
                    street: firma.ulica || prev.street,
                    postalCode: firma.kodPocztowy || prev.postalCode,
                    city: firma.miejscowosc || prev.city,
                }));
            }
        } catch (error) {
            // optional autofill
        }
    };

    const handleSubmit = async () => {
        if (!selectedEventId) {
            showAlert('Wybierz wydarzenie do promowania.');
            return;
        }

        const dateValidationError = validatePromotionStartDate(startDate, selectedEvent, planDurationDays);
        if (dateValidationError) {
            showAlert(dateValidationError);
            return;
        }

        setIsSubmitting(true);
        try {
            const planType = UI_PLAN_TO_API[selectedPlan];
            const startDateIso = startDate ? toPromotionStartDateIso(startDate) : null;

            const order = await ApiService.createPromotionOrder(
                Number(selectedEventId),
                planType,
                formData,
                startDateIso
            );

            if (!order.requiresPayment) {
                showAlert(
                    isScheduledPromotionStatus(order.status)
                        ? `Promocja została zaplanowana.${startDate ? ` Start: ${formatPromotionDate(startDateIso)}.` : ''}`
                        : 'Promocja została aktywowana.'
                );
                onSuccess?.();
                onBack();
                return;
            }

            if (!stripe || !elements) {
                throw new Error('Stripe nie jest gotowy.');
            }

            const cardElement = elements.getElement(CardElement);
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: `${formData.firstName} ${formData.lastName}`.trim(),
                    email: formData.email,
                },
            });

            if (pmError) {
                throw new Error(pmError.message);
            }

            const payment = await ApiService.payPromotionOrder(order.promotionId, paymentMethod.id);
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                payment.clientSecret,
                { payment_method: paymentMethod.id }
            );

            if (confirmError) {
                throw new Error(confirmError.message);
            }

            if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
                const confirmed = await ApiService.confirmPromotionPayment(order.promotionId);
                if (isScheduledPromotionStatus(confirmed?.status)) {
                    showAlert(`Promocja została zaplanowana.${startDate ? ` Start: ${formatPromotionDate(startDateIso)}.` : ''}`);
                } else if (confirmed?.status === 'ACTIVE') {
                    showAlert('Promocja została aktywowana.');
                } else {
                    showAlert('Płatność zakończona. Promocja zostanie aktywowana po potwierdzeniu.');
                }
                onSuccess?.();
                onBack();
            }
        } catch (error) {
            showAlert(getApiErrorMessage(error, 'Nie udało się złożyć zamówienia promocji.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="promotion-order-panel">
            <div className="container">
                <div className="content-wrapper">
                    <button className="back-button" type="button" onClick={onBack}>
                        <ArrowLeft size={20} />
                        Powrót
                    </button>

                    <div className="grid">
                        <div className="form-panel">
                            <h2 className="main-title">Zamówienie promowania</h2>
                            <p className="description">
                                Uzupełnij dane, aby zrealizować zamówienie. Na podany adres e-mail wyślemy fakturę.
                            </p>

                            <div className="section">
                                <h3 className="section-title">1. Wybrane promowanie</h3>
                                <div style={{ position: 'relative' }}>
                                    <button
                                        type="button"
                                        className="select-button"
                                        onClick={() => setIsPlanDropdownOpen(!isPlanDropdownOpen)}
                                    >
                                        <span>{selectedPlan}</span>
                                        <ChevronDown size={20} />
                                    </button>
                                    {isPlanDropdownOpen && (
                                        <div className="plan-dropdown">
                                            {plans.map((plan) => (
                                                <div
                                                    key={plan.id}
                                                    className="plan-dropdown-item"
                                                    onClick={() => {
                                                        setSelectedPlan(plan.id);
                                                        setIsPlanDropdownOpen(false);
                                                    }}
                                                >
                                                    {plan.name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="section">
                                <h3 className="section-title">2. Wybrane wydarzenie</h3>
                                <select
                                    className="input-field full-width"
                                    value={selectedEventId}
                                    onChange={(e) => setSelectedEventId(e.target.value)}
                                    disabled={events.length === 0}
                                >
                                    <option value="">
                                        {events.length === 0
                                            ? 'Brak wydarzeń kwalifikujących się do promocji'
                                            : 'Wybierz wydarzenie'}
                                    </option>
                                    {events.map((event) => (
                                        <option key={event.id} value={event.id}>{event.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="section">
                                <h3 className="section-title">3. Data rozpoczęcia promowania</h3>
                                <PromotionStartDatePicker
                                    value={startDate}
                                    onChange={setStartDate}
                                    minDate={minStartDate}
                                    maxDate={maxStartDate}
                                    durationDays={planDurationDays}
                                    eventEndDate={selectedEvent?.endDate}
                                />
                                <p className="description" style={{ marginTop: '8px' }}>
                                    Pozostaw puste, aby rozpocząć promowanie od razu po płatności.
                                    {' '}Start od dziś. Promowanie kończy się najpóźniej wraz z końcem wydarzenia.
                                </p>
                                {truncationNotice && (
                                    <p className="description schedule-promo-modal-hint--warn" style={{ marginTop: '8px' }}>
                                        {truncationNotice}
                                    </p>
                                )}
                            </div>

                            <div className="section">
                                <h3 className="section-title">4. Twoje dane</h3>
                                <div className="form-fields">
                                    <div className="input-row">
                                        <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="Imię" className="input-field" />
                                        <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Nazwisko" className="input-field" />
                                    </div>
                                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="Numer telefonu" className="input-field full-width" />
                                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Adres e-mail" className="input-field full-width" />
                                </div>
                            </div>

                            <div className="section">
                                <h3 className="section-title invoice-title">Faktura</h3>
                                <div className="form-fields">
                                    <input type="text" name="nip" value={formData.nip} onChange={handleInputChange} onBlur={handleNipBlur} placeholder="NIP" className="input-field full-width" />
                                    <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} placeholder="Nazwa firmy" className="input-field full-width" />
                                    <input type="text" name="street" value={formData.street} onChange={handleInputChange} placeholder="Ulica i numer" className="input-field full-width" />
                                    <div className="input-row">
                                        <input type="text" name="postalCode" value={formData.postalCode} onChange={handleInputChange} placeholder="Kod pocztowy" className="input-field" />
                                        <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="Miasto" className="input-field" />
                                    </div>
                                </div>
                            </div>

                            {total > 0 && (
                                <div className="section">
                                    <h3 className="section-title">5. Płatność kartą</h3>
                                    <div className="card-element-wrapper">
                                        <CardElement />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="summary-column">
                            <div className="summary-panel">
                                <h2 className="summary-title">Podsumowanie do zamówienia</h2>
                                <div className="price-box">
                                    <div className="price-header">
                                        <h3 className="plan-name">Promowanie {selectedPlan}</h3>
                                        <span className="plan-price">{total.toFixed(2)} zł</span>
                                    </div>
                                    <div className="price-details">
                                        <div className="price-row"><span>Kwota netto</span><span>{basePrice.toFixed(2)} zł</span></div>
                                        <div className="price-row"><span>Podatek VAT (23%)</span><span>{vat.toFixed(2)} zł</span></div>
                                        <div className="price-row total"><span>Do zapłaty</span><span>{total.toFixed(2)} zł</span></div>
                                    </div>
                                </div>
                                <button type="button" className="order-button" onClick={handleSubmit} disabled={isSubmitting || isLoadingProfile}>
                                    {isSubmitting ? 'Przetwarzanie...' : isLoadingProfile ? 'Ładowanie danych...' : 'Zamawiam'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function PromotionOrderPanel({ initialPlan = 'Basic', initialEventId = null, initialInvoiceData = null, onBack, onSuccess }) {
    return (
        <Elements stripe={stripePromise}>
            <PromotionOrderForm
                initialPlan={initialPlan}
                initialEventId={initialEventId}
                initialInvoiceData={initialInvoiceData}
                onBack={onBack}
                onSuccess={onSuccess}
            />
        </Elements>
    );
}
